import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ChartContent from 'charts/chart-content/ChartContent';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Pane from 'ui-library/pane/Pane';

import {getOutputNameFromOutput, isSendToDocumentEnabled} from './OutputsUtil';
import {
  AnalysisOutput,
  AnalysisOutputConfig,
  OutputDocumentLayoutConfiguration,
  OutputDocumentLayoutItemType,
  AnalysisOutputType,
} from './Types';

export const PREVIEW_ROW_LIMIT = 5;

export interface OutputPreviewProps {
  outputs: Array<AnalysisOutput>;
  pipeline: PipelineGraphQL;
  clientStateId: string;
  documentLayoutConfiguration: OutputDocumentLayoutConfiguration;
  useAnalysisNamePlaceholder?: boolean;
}

function SingleOutputPreview({
  index,
  outputConfig,
  pipeline,
  clientStateId,
  useAnalysisNamePlaceholder,
}: {
  index: number;
  outputConfig: AnalysisOutputConfig;
  pipeline: PipelineGraphQL;
  clientStateId: string;
  useAnalysisNamePlaceholder: boolean;
}): JSX.Element {
  const analysisName = useAnalysisNamePlaceholder ? 'New Analysis' : pipeline.name;
  if (outputConfig.type === AnalysisOutputType.CHART) {
    return (
      <div key={`preview-${index}`} className={css(styles.chartPreview)}>
        <Pane title={getOutputNameFromOutput(analysisName, outputConfig)}>
          <ChartContent chart={outputConfig.chart} />
        </Pane>
      </div>
    );
  }
  return (
    <div className={css(styles.previewTable)} key={`preview-${index}`}>
      <PersistedDataFrameTableWrapper
        actionOrigin={ActionOrigin.ANALYSIS_TOOL}
        clientStateId={clientStateId}
        dataFrameId={outputConfig.dataFrame.id}
        dataFrameNameOverride={getOutputNameFromOutput(analysisName, outputConfig)}
        rowLimit={PREVIEW_ROW_LIMIT}
        key={`preview-table-${index}`}
      />
    </div>
  );
}

export default function OutputPreview({
  outputs,
  pipeline,
  clientStateId,
  documentLayoutConfiguration,
  useAnalysisNamePlaceholder = false,
}: OutputPreviewProps): JSX.Element {
  if (!isSendToDocumentEnabled()) {
    return (
      <div className={css(styles.previewWrapper)}>
        {outputs.map((output, index) => (
          <SingleOutputPreview
            key={`single-output-preview-${index}`}
            index={index}
            outputConfig={output.outputConfig}
            pipeline={pipeline}
            clientStateId={clientStateId}
            useAnalysisNamePlaceholder={useAnalysisNamePlaceholder}
          />
        ))}
      </div>
    );
  }
  return (
    <div className={css(styles.previewWrapper)}>
      {documentLayoutConfiguration.map((layoutItem, index) => {
        if (layoutItem.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
          return (
            <div key={`preview-wrapper-${index}`} className={css(styles.sectionHeader)}>
              <span className={css(styles.sectionName)}>{layoutItem.title}</span>
              <div className={css(styles.line)} />
            </div>
          );
        } else {
          return (
            <SingleOutputPreview
              key={`single-output-preview-${index}`}
              index={index}
              outputConfig={layoutItem.outputConfig}
              pipeline={pipeline}
              clientStateId={clientStateId}
              useAnalysisNamePlaceholder={useAnalysisNamePlaceholder}
            />
          );
        }
      })}
    </div>
  );
}

const PREVIEW_PADDING = 12;

const styles = StyleSheet.create({
  chartPreview: {
    height: '400px',
    minHeight: '400px',
  },
  previewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    rowGap: 16,
    padding: PREVIEW_PADDING,
  },
  previewTable: {
    height: 'fit-content',
  },
  resultLimitBannerSpacing: {marginBottom: 12},
  sectionName: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginRight: 6,
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
  },
  line: {
    borderBottom: '1px solid #e0e0e0',
    flexGrow: 1,
  },
});
